import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useReduxDispatch } from 'src/store';
import { MixtureTypeDto } from 'src/store/mixturetypes/mixturetype';
import {
  deleteMixtureType,
  fetchMixtureTypes
} from 'src/store/mixturetypes/mixturetypes.actions';
import { selectMixtureTypes } from 'src/store/mixturetypes/mixturetypes.selectors';
import { isLoadingMixtureTypes } from 'src/store/requests/requests.selectors';
import { push } from 'src/utils/navigation';

export const MixtureTypesPage = () => {
  const [mixtureType, setMixtureType] =
    useState<Nullable<MixtureTypeDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const mixtureTypes = useSelector(selectMixtureTypes);
  const loadingMixtureTypes = useSelector(isLoadingMixtureTypes);

  useEffect(() => {
    dispatch(fetchMixtureTypes());
  }, [dispatch]);

  const onCreate = () => {
    push('/mixturetypes/create', navigate, RouteAnimation.SlideRight);
  };

  const onEdit = async (mixtureType: MixtureTypeDto) => {
    push(
      `/mixturetypes/${mixtureType.id}`,
      navigate,
      RouteAnimation.SlideRight
    );
  };

  const onCancel = () => {
    setMixtureType(null);
    setShowDeleteModal(false);
  };

  const onDelete = (mixtureType: MixtureTypeDto) => {
    setMixtureType(mixtureType);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (mixtureType) {
      await dispatch(deleteMixtureType(mixtureType.id, mixtureType));
      setMixtureType(null);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('mixtureType.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('mixtureType.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('mixtureType.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={mixtureTypes}
        loading={loadingMixtureTypes}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'name', dir: 'asc' }]}
      >
        <Column
          field="name"
          title={t('props.name')}
          footerCell={() => (
            <GridCountCell
              count={mixtureTypes?.length}
              label={t('mixtureType.plural')}
            />
          )}
        />
        <Column field="description" title={t('props.description')} />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('mixtureType.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('mixtureType.delete')} <q>{mixtureType?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
